import React, { useRef, useState, useMemo } from "react";
import ReactECharts from "echarts-for-react";
import { FaDownload, FaChartBar, FaChartLine } from "react-icons/fa";
import { RiResetLeftLine } from "react-icons/ri";
import { secondsToHHMM } from "../Utilitie";

const StackedTrendChart = ({
  data = [],
  xKey,
  stackKeys = [],
  height = "400px",
  title = "",
  colors = [],
  singleValue = false,
  isTimeChart = false, // only where HH:MM format is required
  showTotals = true, // only where Totals to be shown above the bars :))
  setSelectedLineBar, // ADD
  barName,
}) => {
  const chartRef = useRef(null);
  const [chartType, setChartType] = useState("bar");

  // Legend state
  const [legendState, setLegendState] = useState(
    stackKeys.reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {}),
  );

  const colorPalette = colors.length
    ? colors
    : [
        "#1E40AF",
        "#4879ff",
        "#3aacee",
        "#0f7696",
        "#7b93ff",
        "#029494",
        "#008cff",
      ];

  const option = useMemo(() => {
    if (!data?.length || !xKey || !stackKeys?.length) return {};

    const xAxisData = data.map((item) => item?.[xKey]);

    // Normal bar/line series (ALL series must exist)
    const series = stackKeys.map((key, index) => ({
      name: key,
      type: chartType,
      stack: chartType === "bar" ? "total" : undefined,
      smooth: chartType === "line",
      itemStyle: {
        color: colorPalette[index % colorPalette.length],
        opacity: 0.85,
      },
      emphasis: {
        itemStyle: { opacity: 1 },
      },
      data: data.map((item) => Number(item?.[key] || 0)),
    }));

    // Flow ribbon series
    const flowSeries = {
      type: "custom",
      coordinateSystem: "cartesian2d",
      renderItem: (params, api) => {
        const elements = [];
        const categories = data.length;

        for (let j = 1; j < categories; j++) {
          let prevStackSum = 0;
          let currStackSum = 0;

          const prevX = api.coord([j - 1, 0])[0];
          const currX = api.coord([j, 0])[0];
          const categoryWidth = Math.abs(currX - prevX);
          const barWidth = categoryWidth * 0.68;
          const halfWidth = barWidth / 2;

          stackKeys.forEach((key, i) => {
            // Skip hidden legend
            if (!legendState[key]) return;

            const prevVal = Number(data[j - 1]?.[key] || 0);
            const currVal = Number(data[j]?.[key] || 0);

            if (prevVal === 0 && currVal === 0) {
              prevStackSum += prevVal;
              currStackSum += currVal;
              return;
            }

            const prevBottom = prevStackSum;
            const prevTop = prevStackSum + prevVal;

            const currBottom = currStackSum;
            const currTop = currStackSum + currVal;

            prevStackSum += prevVal;
            currStackSum += currVal;

            const prevTopCoord = api.coord([j - 1, prevTop]);
            const prevBottomCoord = api.coord([j - 1, prevBottom]);
            const currTopCoord = api.coord([j, currTop]);
            const currBottomCoord = api.coord([j, currBottom]);

            elements.push({
              type: "polygon",
              shape: {
                points: [
                  [prevTopCoord[0] + halfWidth, prevTopCoord[1]],
                  [currTopCoord[0] - halfWidth, currTopCoord[1]],
                  [currBottomCoord[0] - halfWidth, currBottomCoord[1]],
                  [prevBottomCoord[0] + halfWidth, prevBottomCoord[1]],
                ],
              },
              style: {
                fill: colorPalette[i % colorPalette.length],
                opacity: 0.35,
                blend: "source-over",
              },
              silent: true,
            });
          });
        }

        return {
          type: "group",
          children: elements,
        };
      },
      data: [0],
    };
    const totals = data.map((item) =>
      stackKeys.reduce((sum, key) => sum + Number(item?.[key] || 0), 0),
    );
    const totalSeries = {
      name: "Total",
      type: "bar",
      itemStyle: { color: "transparent" },
      label: {
        show: showTotals,
        position: "top",
        fontWeight: "bold",
        formatter: (params) => {
          const total = totals[params.dataIndex];
          return isTimeChart ? secondsToHHMM(total) : total;
        },
      },
      legendHoverLink: false,
      tooltip: { show: false },
      emphasis: { disabled: true },
      data: totals.map((T) => T),
      barGap: "-100%",
    };
    return {
      tooltip: {
        trigger: "axis",
        formatter: (params) => {
          let total = 0;
          let tooltip = `${params[0].axisValue}<br/>`;

          params.forEach((p) => {
            total += p.value;

            const value = isTimeChart ? secondsToHHMM(p.value) : p.value;

            tooltip += `
        <span style="display:inline-block;margin-right:5px;
        border-radius:10px;width:9px;height:9px;
        background-color:${p.color}"></span>
        ${p.seriesName}: ${value}<br/>
      `;
          });

          const totalValue = isTimeChart ? secondsToHHMM(total) : total;

          tooltip += `<b>Total: ${totalValue}</b>`;

          return tooltip;
        },
      },

      legend: {
        top: -1,
        left: "center",
        data: stackKeys,
      },

      grid: {
        top: 100,
        left: 55,
        right: 20,
        bottom: 20,
      },

      xAxis: {
        type: "category",
        data: xAxisData,
      },

      yAxis: {
        type: "value",
        axisLabel: {
          formatter: (value) => (isTimeChart ? secondsToHHMM(value) : value),
        },
      },

      series:
        chartType === "bar" ? [...series, totalSeries, flowSeries] : series,
    };
  }, [data, xKey, stackKeys, legendState, chartType]);

  const downloadChart = () => {
    const chart = chartRef.current.getEchartsInstance();

    const url = chart.getDataURL({
      type: "png",
      pixelRatio: 2,
      backgroundColor: "#fff",
    });

    const link = document.createElement("a");
    link.href = url;
    link.download = `${title || "chart"}.png`;
    link.click();
  };

  const handleChartChange = (type) => {
    if (type === "reset") {
      setChartType("bar");
    } else {
      setChartType(type);
    }
  };
  const onChartClick = (params) => {
    // if (!setSelectedLineBar || !barName) return;

    const obj = {
      barName: params.name,
      barValue: params.value,
      seriesName: params.seriesName,
    };

    setSelectedLineBar(obj);
  };
  const onEvents = {
    legendselectchanged: (params) => {
      setLegendState(params.selected);
    },

    click: onChartClick,
  };
  return (
    <div style={{ position: "relative" }}>
      {!singleValue && (
        <div
          className="d-flex"
          style={{
            position: "absolute",
            top: "-35px",
            right: "0",
            zIndex: 10,
          }}
        >
          <button
            className="btn btn-sm btn-outline-dark me-1"
            style={{ height: "28px" }}
            onClick={downloadChart}
          >
            <FaDownload />
          </button>

          <button
            className={`btn btn-sm btn-outline-dark me-1 ${
              chartType === "bar" ? "active" : ""
            }`}
            style={{ height: "28px" }}
            onClick={() => handleChartChange("bar")}
          >
            <FaChartBar />
          </button>

          <button
            className={`btn btn-sm btn-outline-dark me-1 ${
              chartType === "line" ? "active" : ""
            }`}
            style={{ height: "28px" }}
            onClick={() => handleChartChange("line")}
          >
            <FaChartLine />
          </button>

          <button
            className="btn btn-sm btn-outline-dark me-1"
            style={{ height: "28px" }}
            onClick={() => handleChartChange("reset")}
          >
            <RiResetLeftLine />
          </button>
        </div>
      )}
      <ReactECharts
        ref={chartRef}
        option={option}
        style={{ height, width: "100%" }}
        onEvents={onEvents}
      />
    </div>
  );
};

export default StackedTrendChart;
